import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "../shared/stores/RootStore";
import { City } from "../survey/models/City";
import { BBox, Feature, Polygon } from "geojson";
import { OsmData, OsmWay } from "../map/models/OsmData";
import { toast } from "../shared/views/CustomToaster";
import { CityApi } from "../map/services/CityApi";
import { AdminOsmApi } from "../map/services/AdminOsmApi";
import { JobStateApi } from "../shared/services/JobStateApi";
import { JobStatus } from "../shared/models/JobState";
import { AdminCityApi } from "../admin/services/AdminCityApi";
import { StreetViewMapSource } from "../map/sources/StreetViewMapSource";
import { addCustomIcon, mapGradientStep } from "../map/utils/map-utils";
import { TranslateOsmTag } from "../map/models/TranslateOsmTag";
import { cameraSvg } from "../shared/assets";
import turf from "../shared/utils/turf";

type AllOsmTags = Record<string, string[]>;
type Tag = {
  key: string;
  value: string;
};

export class OsmToolsStore {
  rootStore;

  map?: mapboxgl.Map;
  city?: City;
  cityBBox?: BBox;
  polygons?: Feature<Polygon>[];
  data?: OsmData;
  allTags?: AllOsmTags;
  selectedWay?: OsmWay;
  highlightedTag?: Tag;
  streetViewMapSource?: StreetViewMapSource;
  querying = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.city = undefined;
      this.cityBBox = undefined;
      this.polygons = undefined;
      this.data = undefined;
      this.map = undefined;
      this.allTags = undefined;
      this.selectedWay = undefined;
      this.highlightedTag = undefined;
      this.streetViewMapSource = undefined;
      this.querying = false;
    });
  }

  initStreetViewSource(map: mapboxgl.Map) {
    this.streetViewMapSource?.clear();
    this.streetViewMapSource = new StreetViewMapSource(map);

    addCustomIcon(map, "camera-icon", cameraSvg, 30);
  }

  registerMap(map: mapboxgl.Map) {
    runInAction(() => {
      this.map = map;
    });
  }

  selectWay(wayId: number | undefined) {
    runInAction(() => {
      this.selectedWay = this.data?.ways?.find((w) => w.id === wayId);
    });
  }

  highlightTag(key: string, value: string) {
    runInAction(() => {
      this.highlightedTag = { key, value };
    });
  }

  async fetchCity(cityId: string) {
    await CityApi.get(cityId!).then(({ data }) => {
      runInAction(() => {
        this.city = data;
      });
    });
  }

  async fetchData(cityId: string, cancelIf?: () => boolean) {
    const nodes = await CityApi.getOsmNodes(cityId);
    if (cancelIf?.()) return;

    const ways = await CityApi.getOsmWays(cityId);
    if (cancelIf?.()) return;

    runInAction(() => {
      this.data = { nodes, ways, cityId };
      this.allTags = this.processAllTags(ways);
    });
  }

  async deleteData(cityId: string) {
    await AdminCityApi.deleteOsmData(cityId!).then(() => {
      runInAction(() => {
        this.data = undefined;
        this.allTags = undefined;
      });
    });
  }

  async translateTag(body: TranslateOsmTag) {
    await AdminOsmApi.translateTag(body).then(() => {
      this.fetchData(body.cityId);
    });
  }

  processAllTags(ways: OsmWay[]): AllOsmTags {
    const allTags: AllOsmTags = {};

    ways.forEach(({ tags }) => {
      Object.entries(tags).forEach(([key, value]) => {
        const tagValues = new Set(allTags[key] ?? []);

        tagValues.add(value);

        allTags[key] = [...tagValues];
      });
    });

    return allTags;
  }

  async queryPolygon(cityId: string, polygon: number[][]) {
    this.setQuerying(true);
    await AdminOsmApi.queryPolygon({
      cityId: cityId,
      polygon: polygon,
    }).then(async ({ data: jobStateId }) => {
      await JobStateApi.poll(jobStateId, 2000, (jobState) => {
        if (jobState.status === JobStatus.FAILED) {
          toast("error", "Query failed");
        } else if (jobState.status === JobStatus.FINISHED) {
          toast("success", "Query done");

          this.fetchData(cityId!);
        }
      });

      this.setQuerying(false);
    });
  }

  setQuerying(value: boolean) {
    runInAction(() => {
      this.querying = value;
    });
  }

  searchPolygon() {
    const { name, country } = this.city!;

    AdminOsmApi.searchPolygon(name, country).then(({ data }) => {
      const cityPolygon = data.features[0];
      if (cityPolygon) {
        runInAction(() => {
          this.cityBBox = cityPolygon.bbox;

          this.setPolygons(splitMultiPolygon(cityPolygon));
        });
      } else {
        toast("error", "Unable to find city polygon");
      }
    });
  }

  async splitPolygon(index: number) {
    const polygonToSplit = this.polygons?.[index];

    if (!polygonToSplit) {
      console.warn("No polygon with index", index);
      return;
    }

    const [minX, minY, maxX, maxY] = turf.bbox(polygonToSplit);

    // split along a vertical line down the middle
    const midX = (minX + maxX) / 2;
    const leftBox = turf.bboxPolygon([minX, minY, midX, maxY]);
    const rightBox = turf.bboxPolygon([midX, minY, maxX, maxY]);

    const leftPart = turf.intersect(
      turf.featureCollection([polygonToSplit, leftBox]),
    );
    const rightPart = turf.intersect(
      turf.featureCollection([polygonToSplit, rightBox]),
    );

    if (!leftPart || !rightPart) {
      console.warn(
        "One of the parts are null. Left:",
        leftPart,
        "Right:",
        rightPart,
      );
      return;
    }

    const newPolygons = [leftPart, rightPart]
      .filter(Boolean)
      .flatMap((parts) => splitMultiPolygon(parts));

    var leftSlice = this.polygons!.slice(0, index);
    var rightSlice = this.polygons!.slice(index + 1);

    this.setPolygons([...leftSlice, ...newPolygons, ...rightSlice]);
  }

  setPolygons(polygons: Feature<Polygon>[]) {
    runInAction(() => {
      this.polygons = polygons.map((p, index) => {
        p.properties!.color =
          polygons.length > 1
            ? mapGradientStep(polygons.length, index)
            : undefined;
        return p;
      });
    });
  }
}

const splitMultiPolygon = (feature: Feature): Feature<Polygon>[] => {
  if (feature.geometry.type === "Polygon") {
    return [feature as Feature<Polygon>];
  } else if (feature.geometry.type === "MultiPolygon") {
    const polygonParts = feature.geometry.coordinates;
    const nParts = polygonParts.length;

    return polygonParts.map((coords, index) => {
      return turf.polygon(coords, {
        ...feature.properties,
        color: nParts > 1 ? mapGradientStep(nParts, index) : undefined,
      });
    });
  }

  return [];
};
